import { StageFormSchema, StageFormValues } from '../types/stageForms';

// Stage ids are generated per workflow, so schemas are keyed by stage name until
// they are stored against the stage itself. Everything here is plain data, so
// moving it into the workflow definition later is a change of source, not shape.
const normalise = (stageName: string) => stageName.trim().toLowerCase();

const FALLBACK: StageFormSchema = {
  title: 'Stage notes',
  description: 'Record what was done at this stage before moving it on.',
  fields: [
    { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'What was done at this stage?' },
  ],
};

const SCHEMAS: Record<string, StageFormSchema> = {
  'receive & register enquiry': {
    title: 'Receive & register enquiry',
    description: 'Record the enquiry as received from the client.',
    fields: [
      { name: 'enquiryNumber', label: 'Enquiry number', type: 'text', required: true, placeholder: 'ENQ-0000', help: 'Assigned when you save. Change it only if this enquiry already has a number.' },
      { name: 'client', label: 'Client', type: 'text', required: true },
      { name: 'project', label: 'Project', type: 'text', required: true },
      { name: 'contact', label: 'Client contact', type: 'text' },
      { name: 'receivedOn', label: 'Received on', type: 'date', required: true },
      { name: 'requestedSubmission', label: 'Requested submission date', type: 'date' },
      { name: 'scopeSummary', label: 'Scope summary', type: 'textarea', required: true },
    ],
  },
  'assign team': {
    title: 'Assign team',
    description: 'Appoint the owners and confirm the dates.',
    fields: [
      { name: 'siteVisitOwner', label: 'Site visit owner', type: 'assignee', required: true, targetStage: 'Site Visit & Scope Capture' },
      { name: 'engineer', label: 'Engineer / estimator', type: 'assignee', required: true, targetStage: 'Verify Site Information' },
      { name: 'siteVisitDue', label: 'Site visit due', type: 'date', required: true },
      { name: 'quotationDue', label: 'Quotation due', type: 'date', required: true },
    ],
  },
  'site visit & scope capture': {
    title: 'Site visit & scope',
    description: 'Record what was measured and observed on site.',
    fields: [
      { name: 'visitDate', label: 'Visit date', type: 'date', required: true },
      { name: 'attendedBy', label: 'Attended by', type: 'text', required: true },
      { name: 'measurements', label: 'Measurements & quantity take-off', type: 'textarea', required: true },
      { name: 'existingConditions', label: 'Existing conditions', type: 'textarea' },
      { name: 'accessRestrictions', label: 'Access & working-hour restrictions', type: 'textarea' },
      { name: 'permitsSafety', label: 'Permits & safety constraints', type: 'textarea' },
      { name: 'openQuestions', label: 'Open questions', type: 'textarea' },
    ],
  },
  'verify site information': {
    title: 'Verify site information',
    description: 'Check the site record against the enquiry, then list what procurement must price.',
    fields: [
      { name: 'quantitiesVerified', label: 'Quantities verified against the enquiry', type: 'checkbox', required: true },
      { name: 'gapsResolved', label: 'Gaps resolved with client or site team', type: 'textarea' },
      { name: 'assumptions', label: 'Assumptions', type: 'textarea', required: true },
      { name: 'exclusions', label: 'Proposed exclusions', type: 'textarea', required: true },
      {
        name: 'materials',
        label: 'Items to be priced',
        type: 'table',
        required: true,
        addLabel: 'Add item',
        help: 'Procurement prices exactly these items, so list every product the quotation needs.',
        columns: [
          { name: 'item', label: 'Item / product', type: 'text', width: 'wide' },
          { name: 'specification', label: 'Specification', type: 'text', width: 'wide' },
          { name: 'unit', label: 'Unit', type: 'text', width: 'narrow' },
          { name: 'quantity', label: 'Qty', type: 'number', width: 'narrow' },
        ],
      },
    ],
  },
  'obtain supplier prices': {
    title: 'Obtain supplier prices',
    description: 'Price each item the engineer listed, then recommend the basis.',
    fields: [
      {
        name: 'pricedItems',
        label: 'Supplier pricing',
        type: 'table',
        required: true,
        addLabel: 'Add item',
        help: 'Carried over from the engineer. Add a row for anything extra that must be priced.',
        prefillFrom: {
          stage: 'Verify Site Information',
          field: 'materials',
          columns: ['item', 'specification', 'unit', 'quantity'],
        },
        columns: [
          { name: 'item', label: 'Item / product', type: 'text', width: 'wide' },
          { name: 'specification', label: 'Specification', type: 'text', width: 'wide' },
          { name: 'unit', label: 'Unit', type: 'text', width: 'narrow' },
          { name: 'quantity', label: 'Qty', type: 'number', width: 'narrow' },
          { name: 'supplier', label: 'Supplier', type: 'text' },
          { name: 'unitPrice', label: 'Unit price', type: 'number', width: 'narrow' },
          { name: 'leadTime', label: 'Lead time', type: 'text', width: 'narrow' },
        ],
      },
      { name: 'quotesRequested', label: 'Quotations requested', type: 'number', required: true },
      { name: 'quotesReceived', label: 'Quotations received', type: 'number', required: true },
      { name: 'shortfallReason', label: 'Reason if fewer than three', type: 'textarea', help: 'Required when fewer than three comparable quotations were obtained.' },
      { name: 'comparison', label: 'Comparison (price, scope, lead time, validity, warranty)', type: 'textarea', required: true },
      { name: 'recommendedSupplier', label: 'Recommended supplier / basis', type: 'text', required: true },
    ],
  },
  'prepare & check boq': {
    title: 'Prepare & check BOQ',
    description: 'Build the bill of quantities from the supplier pricing, then apply the margin.',
    fields: [
      {
        name: 'boqLines',
        label: 'Bill of quantities',
        type: 'table',
        required: true,
        addLabel: 'Add BOQ line',
        help: 'Started from the supplier pricing. Add, edit or remove lines as the BOQ requires.',
        prefillFrom: {
          stage: 'Obtain Supplier Prices',
          field: 'pricedItems',
          columns: ['item', 'specification', 'unit', 'quantity', 'unitPrice'],
        },
        columns: [
          { name: 'item', label: 'Description', type: 'text', width: 'wide' },
          { name: 'specification', label: 'Specification', type: 'text', width: 'wide' },
          { name: 'unit', label: 'Unit', type: 'text', width: 'narrow' },
          { name: 'quantity', label: 'Qty', type: 'number', width: 'narrow' },
          { name: 'unitPrice', label: 'Rate', type: 'number', width: 'narrow' },
          { name: 'amount', label: 'Amount', type: 'number', width: 'narrow' },
        ],
      },
      { name: 'boqReference', label: 'BOQ reference', type: 'text', required: true, help: 'Suggested when this stage opens. Change it if this BOQ already has a reference.' },
      { name: 'materialCost', label: 'Material cost', type: 'number', required: true },
      { name: 'labourCost', label: 'Labour & equipment cost', type: 'number', required: true },
      { name: 'overheads', label: 'Overheads', type: 'number' },
      { name: 'profitPercent', label: 'Profit %', type: 'number', required: true },
      { name: 'sellingPrice', label: 'Selling price', type: 'number', required: true },
      { name: 'validityDays', label: 'Validity (days)', type: 'number' },
      { name: 'arithmeticChecked', label: 'Arithmetic and scope completeness checked', type: 'checkbox', required: true },
    ],
  },
  'manager approval': {
    title: 'Manager approval',
    description: 'Review the BOQ as submitted. Approve it, or send it back with a note saying what to change.',
    fields: [
      { name: 'decision', label: 'Decision', type: 'select', required: true, options: [{ value: 'approved', label: 'Approved to draft quotation' }] },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea', help: 'Anything the team should know. To ask for changes, use Send Back instead.' },
    ],
  },
  'draft quotation': {
    title: 'Draft quotation',
    description: 'Prepare the client quotation from the approved BOQ.',
    fields: [
      { name: 'quotationNumber', label: 'Quotation number', type: 'text', required: true },
      { name: 'revision', label: 'Revision', type: 'text', required: true },
      { name: 'quotedAmount', label: 'Quoted amount', type: 'number', required: true },
      { name: 'validity', label: 'Validity', type: 'text' },
      { name: 'exclusions', label: 'Exclusions stated', type: 'textarea', required: true },
      { name: 'templateUsed', label: 'Current company template used', type: 'checkbox', required: true },
    ],
  },
  'engineer verification': {
    title: 'Engineer verification',
    description: 'Confirm the draft matches the approved costing.',
    fields: [
      { name: 'matchesCosting', label: 'Scope, quantities and price match the approved costing', type: 'checkbox', required: true },
      { name: 'termsChecked', label: 'Schedule, validity and terms checked', type: 'checkbox', required: true },
      { name: 'discrepancies', label: 'Discrepancies found', type: 'textarea' },
    ],
  },
  'senior management approval': {
    title: 'Senior management approval',
    description: 'Review the quotation as drafted. Approve it for issue, or send it back with a note.',
    fields: [
      { name: 'decision', label: 'Decision', type: 'select', required: true, options: [{ value: 'approved', label: 'Approved for issue' }] },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea', help: 'Anything the team should know. To ask for changes, use Send Back instead.' },
    ],
  },
  'issue to client & close': {
    title: 'Issue to client & close',
    description: 'Record the issue against the enquiry.',
    fields: [
      { name: 'issuedOn', label: 'Issue date', type: 'date', required: true },
      { name: 'recipient', label: 'Sent to', type: 'text', required: true },
      {
        name: 'quotationNumber',
        label: 'Quotation number',
        type: 'text',
        required: true,
        prefillFrom: { stage: 'Draft quotation' },
      },
      {
        name: 'revision',
        label: 'Revision',
        type: 'text',
        required: true,
        prefillFrom: { stage: 'Draft quotation' },
      },
      { name: 'sentEmailSaved', label: 'Sent email saved to the enquiry folder', type: 'checkbox', required: true },
      { name: 'outcome', label: 'Outcome', type: 'select', options: [
        { value: 'issued', label: 'Issued' },
        { value: 'won', label: 'Won' },
        { value: 'lost', label: 'Lost' },
      ] },
    ],
  },
  'receive & register application': {
    title: 'Receive & register application',
    description: 'Record the loan application as received.',
    fields: [
      { name: 'applicationNumber', label: 'Application number', type: 'text', required: true, placeholder: 'APP-0000' },
      { name: 'applicant', label: 'Applicant', type: 'text', required: true },
      { name: 'product', label: 'Loan product', type: 'text', required: true },
      { name: 'amountRequested', label: 'Amount requested', type: 'number', required: true },
      { name: 'contact', label: 'Applicant contact', type: 'text' },
      { name: 'receivedOn', label: 'Received on', type: 'date', required: true },
      { name: 'purpose', label: 'Purpose', type: 'textarea', required: true },
    ],
  },
  'assign loan officer': {
    title: 'Assign loan officer',
    description: 'Appoint the officer who will draft the offer, and confirm the dates.',
    fields: [
      { name: 'loanOfficer', label: 'Loan officer', type: 'assignee', required: true, targetStage: 'Draft Loan Offer' },
      { name: 'assessmentDue', label: 'Credit assessment due', type: 'date', required: true },
      { name: 'offerDue', label: 'Offer due', type: 'date', required: true },
    ],
  },
  'collect & verify documents': {
    title: 'Collect & verify documents',
    description: 'Record each document received and whether it checks out.',
    fields: [
      {
        name: 'documents',
        label: 'Documents',
        type: 'table',
        required: true,
        addLabel: 'Add document',
        help: 'List every document the application needs, and the status of each one.',
        columns: [
          { name: 'document', label: 'Document', type: 'text', width: 'wide' },
          { name: 'status', label: 'Status', type: 'text' },
          { name: 'remarks', label: 'Remarks', type: 'text', width: 'wide' },
        ],
      },
      { name: 'identityVerified', label: 'Identity verified', type: 'checkbox', required: true },
      { name: 'incomeVerified', label: 'Income verified', type: 'checkbox', required: true },
      { name: 'addressVerified', label: 'Address verified', type: 'checkbox', required: true },
    ],
  },
  'assess creditworthiness': {
    title: 'Assess creditworthiness',
    description: 'Score the application and recommend whether an offer should be drafted.',
    fields: [
      { name: 'creditScore', label: 'Credit score', type: 'number', required: true },
      { name: 'incomeConfirmed', label: 'Income confirmed', type: 'number', required: true },
      { name: 'existingObligations', label: 'Existing obligations', type: 'number' },
      { name: 'riskRating', label: 'Risk rating', type: 'select', required: true, options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
      ] },
      { name: 'recommendation', label: 'Recommendation', type: 'select', required: true, options: [
        { value: 'proceed', label: 'Proceed to offer' },
        { value: 'decline', label: 'Decline' },
      ] },
      { name: 'assessmentNotes', label: 'Assessment notes', type: 'textarea', required: true },
    ],
  },
  'draft loan offer': {
    title: 'Draft loan offer',
    description: 'Prepare the offer from the credit assessment.',
    fields: [
      { name: 'offerReference', label: 'Offer reference', type: 'text', required: true },
      { name: 'offeredAmount', label: 'Offered amount', type: 'number', required: true },
      { name: 'interestRate', label: 'Interest rate %', type: 'number', required: true },
      { name: 'tenureMonths', label: 'Tenure (months)', type: 'number', required: true },
      { name: 'validUntil', label: 'Valid until', type: 'date' },
      { name: 'conditions', label: 'Conditions', type: 'textarea', required: true },
    ],
  },
  'complete agreement & disbursement': {
    title: 'Complete agreement & disbursement',
    description: 'Record the signed agreement and the disbursement.',
    fields: [
      { name: 'agreementNumber', label: 'Agreement number', type: 'text', required: true },
      {
        name: 'approvedAmount',
        label: 'Approved amount',
        type: 'number',
        required: true,
        prefillFrom: { stage: 'Draft Loan Offer', field: 'offeredAmount' },
      },
      { name: 'signedOn', label: 'Signed on', type: 'date', required: true },
      { name: 'disbursedOn', label: 'Disbursed on', type: 'date', required: true },
      { name: 'disbursementAccount', label: 'Disbursement account', type: 'text', required: true },
      { name: 'agreementSigned', label: 'Signed agreement is on file', type: 'checkbox', required: true },
    ],
  },
  'close application': {
    title: 'Close application',
    description: 'Close the application and record the outcome.',
    fields: [
      { name: 'recipient', label: 'Sent to', type: 'text', required: true },
      { name: 'closedOn', label: 'Closed on', type: 'date', required: true },
      {
        name: 'applicationNumber',
        label: 'Application number',
        type: 'text',
        required: true,
        prefillFrom: { stage: 'Receive & Register Application' },
      },
      { name: 'customerNotified', label: 'Applicant notified', type: 'checkbox', required: true },
      { name: 'outcome', label: 'Outcome', type: 'select', options: [
        { value: 'disbursed', label: 'Disbursed' },
        { value: 'withdrawn', label: 'Withdrawn' },
        { value: 'declined', label: 'Declined' },
      ] },
      { name: 'closureNotes', label: 'Notes', type: 'textarea' },
    ],
  },
  'identify stock requirement': {
    title: 'Identify stock requirement',
    description: 'Record what needs replenishing, and by when.',
    fields: [
      { name: 'requestNumber', label: 'Request number', type: 'text', required: true, placeholder: 'REQ-0000' },
      { name: 'sku', label: 'SKU', type: 'text', required: true },
      { name: 'itemDescription', label: 'Item', type: 'text', required: true },
      { name: 'quantityRequired', label: 'Quantity required', type: 'number', required: true },
      { name: 'neededBy', label: 'Needed by', type: 'date', required: true },
      { name: 'reason', label: 'Reason', type: 'textarea', required: true },
    ],
  },
  'review & assign request': {
    title: 'Review & assign request',
    description: 'Confirm the request and appoint the buyer who will raise the purchase order.',
    fields: [
      { name: 'buyer', label: 'Buyer', type: 'assignee', required: true, targetStage: 'Obtain Supplier Quotations & Prepare Purchase Order' },
      { name: 'requiredBy', label: 'Required by', type: 'date', required: true },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea' },
    ],
  },
  'validate stock & budget': {
    title: 'Validate stock & budget',
    description: 'Check on-hand stock and confirm the budget covers the replenishment.',
    fields: [
      { name: 'onHand', label: 'Quantity on hand', type: 'number', required: true },
      { name: 'reorderPoint', label: 'Reorder point', type: 'number', required: true },
      { name: 'budgetAvailable', label: 'Budget available', type: 'number', required: true },
      { name: 'budgetApproved', label: 'Budget covers this request', type: 'checkbox', required: true },
      { name: 'validationNotes', label: 'Notes', type: 'textarea' },
    ],
  },
  'obtain supplier quotations & prepare purchase order': {
    title: 'Obtain supplier quotations & prepare purchase order',
    description: 'Compare supplier quotations, then raise the purchase order.',
    fields: [
      {
        name: 'quotations',
        label: 'Supplier quotations',
        type: 'table',
        required: true,
        addLabel: 'Add quotation',
        help: 'Record each quotation received before choosing the supplier.',
        columns: [
          { name: 'supplier', label: 'Supplier', type: 'text', width: 'wide' },
          { name: 'unitPrice', label: 'Unit price', type: 'number', width: 'narrow' },
          { name: 'leadTime', label: 'Lead time', type: 'text', width: 'narrow' },
          { name: 'validity', label: 'Validity', type: 'text' },
        ],
      },
      { name: 'quotesRequested', label: 'Quotations requested', type: 'number', required: true },
      { name: 'quotesReceived', label: 'Quotations received', type: 'number', required: true },
      { name: 'selectedSupplier', label: 'Selected supplier', type: 'text', required: true },
      { name: 'poNumber', label: 'Purchase order number', type: 'text', required: true },
      { name: 'orderQuantity', label: 'Order quantity', type: 'number', required: true },
      { name: 'unitCost', label: 'Unit cost', type: 'number', required: true },
      { name: 'expectedDelivery', label: 'Expected delivery', type: 'date' },
    ],
  },
  'purchase approval': {
    title: 'Purchase approval',
    description: 'Review the purchase order. Approve it, or send it back with a note.',
    fields: [
      { name: 'decision', label: 'Decision', type: 'select', required: true, options: [{ value: 'approved', label: 'Approved to place the order' }] },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea', help: 'Anything the team should know. To ask for changes, use Send Back instead.' },
    ],
  },
  'receive & inspect goods': {
    title: 'Receive & inspect goods',
    description: 'Record what arrived and what passed inspection.',
    fields: [
      { name: 'receivedOn', label: 'Received on', type: 'date', required: true },
      { name: 'quantityReceived', label: 'Quantity received', type: 'number', required: true },
      { name: 'quantityAccepted', label: 'Quantity accepted', type: 'number', required: true },
      { name: 'inspectionResult', label: 'Inspection result', type: 'select', required: true, options: [
        { value: 'accepted', label: 'Accepted' },
        { value: 'partial', label: 'Partial' },
        { value: 'rejected', label: 'Rejected' },
      ] },
      { name: 'discrepancies', label: 'Discrepancies', type: 'textarea' },
    ],
  },
  'update inventory & close': {
    title: 'Update inventory & close',
    description: 'Post the accepted quantity and close the request.',
    fields: [
      { name: 'recipient', label: 'Sent to', type: 'text', required: true },
      { name: 'postedOn', label: 'Posted on', type: 'date', required: true },
      {
        name: 'quantityPosted',
        label: 'Quantity posted',
        type: 'number',
        required: true,
        prefillFrom: { stage: 'Receive & Inspect Goods', field: 'quantityAccepted' },
      },
      { name: 'binLocation', label: 'Bin location', type: 'text' },
      {
        name: 'requestNumber',
        label: 'Request number',
        type: 'text',
        required: true,
        prefillFrom: { stage: 'Identify Stock Requirement' },
      },
      { name: 'requestClosed', label: 'Request closed', type: 'checkbox', required: true },
    ],
  },
  'receive & register claim': {
    title: 'Receive & register claim',
    description: 'Record the claim as received from the customer.',
    fields: [
      { name: 'claimNumber', label: 'Claim number', type: 'text', required: true, placeholder: 'CLM-0000' },
      { name: 'policyNumber', label: 'Policy number', type: 'text', required: true },
      { name: 'claimant', label: 'Claimant', type: 'text', required: true },
      { name: 'contact', label: 'Claimant contact', type: 'text' },
      { name: 'incidentDate', label: 'Incident date', type: 'date', required: true },
      { name: 'receivedOn', label: 'Received on', type: 'date', required: true },
      { name: 'lossDescription', label: 'Loss description', type: 'textarea', required: true },
    ],
  },
  'assign claims handler': {
    title: 'Assign claims handler',
    description: 'Appoint the handler who will collect the evidence.',
    fields: [
      { name: 'claimsHandler', label: 'Claims handler', type: 'assignee', required: true, targetStage: 'Collect Documents & Evidence' },
      { name: 'assessmentDue', label: 'Assessment due', type: 'date', required: true },
    ],
  },
  'verify policy & coverage': {
    title: 'Verify policy & coverage',
    description: 'Confirm the policy is in force and the loss is covered.',
    fields: [
      { name: 'policyActive', label: 'Policy is in force', type: 'checkbox', required: true },
      { name: 'coverageConfirmed', label: 'Loss falls within cover', type: 'checkbox', required: true },
      { name: 'sumInsured', label: 'Sum insured', type: 'number', required: true },
      { name: 'exclusions', label: 'Exclusions that apply', type: 'textarea' },
      { name: 'coverageDecision', label: 'Coverage decision', type: 'select', required: true, options: [
        { value: 'covered', label: 'Covered' },
        { value: 'partial', label: 'Partial' },
        { value: 'not-covered', label: 'Not covered' },
      ] },
    ],
  },
  'collect documents & evidence': {
    title: 'Collect documents & evidence',
    description: 'Record the documents and evidence supporting the claim.',
    fields: [
      {
        name: 'evidence',
        label: 'Evidence',
        type: 'table',
        required: true,
        addLabel: 'Add item',
        help: 'List every document or piece of evidence the assessment will rely on.',
        columns: [
          { name: 'item', label: 'Document / evidence', type: 'text', width: 'wide' },
          { name: 'status', label: 'Status', type: 'text' },
          { name: 'remarks', label: 'Remarks', type: 'text', width: 'wide' },
        ],
      },
      { name: 'evidenceSummary', label: 'Evidence summary', type: 'textarea', required: true },
      { name: 'missingItems', label: 'Still missing', type: 'textarea' },
    ],
  },
  'assess claim & calculate settlement': {
    title: 'Assess claim & calculate settlement',
    description: 'Work out the settlement from the evidence and the cover.',
    fields: [
      { name: 'claimedAmount', label: 'Claimed amount', type: 'number', required: true },
      { name: 'assessedAmount', label: 'Assessed amount', type: 'number', required: true },
      { name: 'deductible', label: 'Deductible', type: 'number' },
      { name: 'settlementAmount', label: 'Settlement amount', type: 'number', required: true },
      { name: 'basis', label: 'Basis of settlement', type: 'textarea', required: true },
    ],
  },
  'process claim payment': {
    title: 'Process claim payment',
    description: 'Pay the approved settlement and record the reference.',
    fields: [
      { name: 'paymentReference', label: 'Payment reference', type: 'text', required: true },
      {
        name: 'paidAmount',
        label: 'Amount paid',
        type: 'number',
        required: true,
        prefillFrom: { stage: 'Assess Claim & Calculate Settlement', field: 'settlementAmount' },
      },
      { name: 'paidOn', label: 'Paid on', type: 'date', required: true },
      { name: 'payee', label: 'Payee', type: 'text', required: true },
      { name: 'paymentRecorded', label: 'Payment recorded against the claim', type: 'checkbox', required: true },
    ],
  },
  'notify customer & close claim': {
    title: 'Notify customer & close claim',
    description: 'Tell the customer the outcome and close the claim.',
    fields: [
      { name: 'recipient', label: 'Sent to', type: 'text', required: true },
      { name: 'notifiedOn', label: 'Notified on', type: 'date', required: true },
      { name: 'notifiedBy', label: 'Notified by', type: 'text', required: true },
      {
        name: 'claimNumber',
        label: 'Claim number',
        type: 'text',
        required: true,
        prefillFrom: { stage: 'Receive & Register Claim' },
      },
      { name: 'customerConfirmed', label: 'Customer confirmed receipt', type: 'checkbox', required: true },
      { name: 'outcome', label: 'Outcome', type: 'select', options: [
        { value: 'paid', label: 'Paid' },
        { value: 'declined', label: 'Declined' },
        { value: 'withdrawn', label: 'Withdrawn' },
      ] },
    ],
  },
};

// "Manager Approval" already belongs to Quotation Preparation. These two keep
// that form intact and supply their own decision only when the workflow is named.
const WORKFLOW_SCHEMAS: Record<string, StageFormSchema> = {
  'loan application processing::manager approval': {
    title: 'Manager approval',
    description: 'Review the loan offer. Approve it, or send it back with a note.',
    fields: [
      { name: 'decision', label: 'Decision', type: 'select', required: true, options: [{ value: 'approved', label: 'Approved to complete the agreement' }] },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea', help: 'Anything the team should know. To ask for changes, use Send Back instead.' },
    ],
  },
  'claim processing::manager approval': {
    title: 'Manager approval',
    description: 'Review the settlement. Approve it for payment, or send it back with a note.',
    fields: [
      { name: 'decision', label: 'Decision', type: 'select', required: true, options: [{ value: 'approved', label: 'Approved for payment' }] },
      { name: 'reviewNotes', label: 'Notes', type: 'textarea', help: 'Anything the team should know. To ask for changes, use Send Back instead.' },
    ],
  },
};

const workflowKey = (workflowName: string, stageName: string) =>
  `${normalise(workflowName)}::${normalise(stageName)}`;

const ENQUIRY_INTAKE = 'receive & register enquiry';
const BOQ_STAGE = 'prepare & check boq';
const DRAFT_QUOTATION = 'draft quotation';

const fourDigits = () => String(Math.floor(Math.random() * 10000)).padStart(4, '0');

const fillIfBlank = (values: StageFormValues, name: string, suggested: string): StageFormValues => {
  const current = values[name];
  if (typeof current === 'string' && current.trim()) return values;
  return { ...values, [name]: suggested };
};

/** Four-digit suffix, so the reference reads BOQ-0000 through BOQ-9999. */
export const randomBoqReference = (): string => `BOQ-${fourDigits()}`;

/** Fills an empty BOQ reference. A value already typed or saved is left as it is. */
export const withSuggestedBoqReference = (
  stageName: string | undefined,
  values: StageFormValues
): StageFormValues => {
  if (normalise(stageName ?? '') !== BOQ_STAGE) return values;
  return fillIfBlank(values, 'boqReference', randomBoqReference());
};

/** Fills an empty quotation number and revision. Saved or typed values stay. */
export const withSuggestedQuotation = (
  stageName: string | undefined,
  values: StageFormValues
): StageFormValues => {
  if (normalise(stageName ?? '') !== DRAFT_QUOTATION) return values;
  return fillIfBlank(
    fillIfBlank(values, 'quotationNumber', `QUOT-${fourDigits()}`),
    'revision',
    `RO-${fourDigits()}`
  );
};

export const isEnquiryIntake = (stageName?: string): boolean =>
  normalise(stageName ?? '') === ENQUIRY_INTAKE;

export const getStageForm = (stageName?: string, workflowName?: string): StageFormSchema => {
  if (stageName && workflowName) {
    const qualified = WORKFLOW_SCHEMAS[workflowKey(workflowName, stageName)];
    if (qualified) return qualified;
  }
  return (stageName ? SCHEMAS[normalise(stageName)] : undefined) ?? FALLBACK;
};

export const hasStageForm = (stageName?: string, workflowName?: string): boolean => {
  if (stageName && workflowName && WORKFLOW_SCHEMAS[workflowKey(workflowName, stageName)]) return true;
  return Boolean(stageName && SCHEMAS[normalise(stageName)]);
};

// Stages whose owner is appointed earlier by someone else — the team lead naming
// the engineer, for example. Nobody downstream gets to pick for these.
const APPOINTED_STAGES = new Set(
  [...Object.values(SCHEMAS), ...Object.values(WORKFLOW_SCHEMAS)]
    .flatMap((schema) => schema.fields)
    .filter((field) => field.type === 'assignee' && field.targetStage)
    .map((field) => normalise(field.targetStage!))
);

export const isAppointedStage = (stageName?: string): boolean =>
  Boolean(stageName && APPOINTED_STAGES.has(normalise(stageName)));

/** Field name to its label, for reading back what an earlier stage submitted. */
export const stageFieldLabels = (stageName?: string, workflowName?: string): Record<string, string> =>
  getStageForm(stageName, workflowName).fields.reduce<Record<string, string>>(
    (labels, field) => ({
      ...labels,
      [field.name]: field.label,
      ...(field.columns ?? []).reduce<Record<string, string>>(
        (cols, column) => ({ ...cols, [column.name]: column.label }),
        {}
      ),
    }),
    {}
  );
